import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

export default function CharCount() {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  const stats = useMemo(() => {
    const chars = text.split("");
    const chinese = chars.filter((c) => /[\u4e00-\u9fa5]/.test(c)).length;
    const english = chars.filter((c) => /[a-zA-Z]/.test(c)).length;
    const numbers = chars.filter((c) => /[0-9]/.test(c)).length;
    const spaces = chars.filter((c) => /\s/.test(c)).length;
    const punctuation = chars.filter((c) =>
      /[，。！？、；：""''（）【】《》,.!?;:'"()\[\]{}]/.test(c)
    ).length;

    return {
      total: text.length,
      chars: chars.filter((c) => c.trim()).length,
      words: text.trim() ? text.trim().split(/\s+/).length : 0,
      lines: text ? text.split("\n").length : 0,
      chinese,
      english,
      numbers,
      spaces,
      punctuation,
      readingTime: Math.ceil(text.length / 500),
    };
  }, [text]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  const clearText = () => {
    setText("");
  };

  return (
    <main className="max-w-4xl mx-auto" role="main" aria-label="Character Counter Tool">
      <header className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
          {t("tools.char-count", "Character Counter")}
        </h1>
        <p className="text-gray-600" id="tool-description">
          {t("charCount.desc", "Real-time text character statistics")}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: Input Area */}
        <section className="space-y-4" aria-label="Text Input Area">
          <div className="relative">
            <label htmlFor="text-input" className="sr-only">
              {t("charCount.placeholder", "Enter or paste text here...")}
            </label>
            <Textarea
              id="text-input"
              className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 resize-none text-gray-800 leading-relaxed"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("charCount.placeholder", "Enter or paste text here...")}
              aria-describedby="tool-description"
            />
            <div className="absolute bottom-4 right-4 flex gap-2">
              <Button
                variant={copied ? "default" : "secondary"}
                size="sm"
                onClick={handleCopy}
                className={copied ? "bg-green-500 text-white hover:bg-green-600" : ""}
              >
                {copied ? "✓ Copied" : "📋 Copy"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={clearText}
                className="bg-red-100 text-red-700 hover:bg-red-200"
              >
                🗑️ Clear
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" role="region" aria-label="Quick Statistics">
            <QuickStat label="Total Characters" value={stats.total} icon="📝" color="blue" />
            <QuickStat label="Words" value={stats.words} icon="📖" color="green" />
          </div>
        </section>

        {/* Right: Statistics Dashboard */}
        <section className="space-y-6" aria-label="Detailed Statistics Dashboard">
          <h2 className="text-xl font-semibold text-gray-800 mb-4" id="stats-heading">
            📊 {t("charCount.statistics", "Statistics")}
          </h2>

          <div className="grid grid-cols-2 gap-4" role="region" aria-labelledby="stats-heading">
            <StatCard label={t("charCount.total", "Total Characters")} value={stats.total} icon="📊" color="indigo" />
            <StatCard label={t("charCount.chars", "Characters (no spaces)")} value={stats.chars} icon="🔤" color="blue" />
            <StatCard label={t("charCount.words", "Words")} value={stats.words} icon="📝" color="green" />
            <StatCard label={t("charCount.lines", "Lines")} value={stats.lines} icon="📄" color="purple" />
          </div>

          {/* Character Composition */}
          <div
            className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6"
            role="region"
            aria-label="Character Composition Chart"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              🧩 {t("charCount.composition", "Character Composition")}
            </h3>
            <div className="space-y-3">
              <ProgressBar label={t("charCount.chinese", "Chinese Characters")} value={stats.chinese} total={stats.total} color="red" />
              <ProgressBar label={t("charCount.english", "English Letters")} value={stats.english} total={stats.total} color="blue" />
              <ProgressBar label={t("charCount.numbers", "Numbers")} value={stats.numbers} total={stats.total} color="green" />
              <ProgressBar label={t("charCount.spaces", "Spaces")} value={stats.spaces} total={stats.total} color="gray" />
              <ProgressBar label={t("charCount.punctuation", "Punctuation")} value={stats.punctuation} total={stats.total} color="yellow" />
            </div>
          </div>

          {/* Reading Time */}
          {stats.total > 0 && (
            <div
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6"
              role="note"
              aria-label="Reading Time Estimation"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                ⏱️ {t("charCount.readingTime", "Estimated Reading Time")}
              </h3>
              <p className="text-3xl font-bold text-indigo-600">
                {stats.readingTime} {t("charCount.minutes", "minutes")}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {t("charCount.readingSpeed", "Calculated at 500 chars/minute")}
              </p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function QuickStat({ label, value, icon, color }: { label: string; value: number; icon: string; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
  };

  return (
    <div className={`p-4 rounded-xl border-2 ${colorClasses[color]} transition-all duration-200`} role="status" aria-label={`${label}: ${value.toLocaleString()}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value.toLocaleString()}</div>
    </div>
  );
}

function StatCard({ label, value, icon, color }: { label: string; value: number; icon: string; color: string }) {
  const colorClasses: Record<string, string> = {
    indigo: "bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200",
    blue: "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200",
    green: "bg-gradient-to-br from-green-50 to-green-100 border-green-200",
    purple: "bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200",
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`p-4 rounded-xl border-2 ${colorClasses[color]} transition-all duration-200 shadow-sm hover:shadow-md`}
      role="status"
      aria-label={`${label}: ${value.toLocaleString()}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-3xl font-bold text-gray-800 mb-1">{value.toLocaleString()}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </motion.div>
  );
}

function ProgressBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const colorClasses: Record<string, string> = {
    red: "bg-red-500",
    blue: "bg-blue-500",
    green: "bg-green-500",
    gray: "bg-gray-500",
    yellow: "bg-yellow-500",
  };

  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={`${label}: ${value} (${percentage.toFixed(1)}%)`}
    >
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-600">
          {value} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={`h-full ${colorClasses[color]} rounded-full`}
        />
      </div>
    </div>
  );
}
